import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

// UX-POLISH-01 #1/#2: one shared, validated field for every form in the app — a required field
// gets a red asterisk, and every field validates AS YOU TYPE once it's been touched once
// (email needs an @ + domain, numeric fields hard-block letters at the keystroke level, not just
// on submit). Deliberately a thin wrapper around the existing shadcn `Input`/`Label` — never a
// new visual language, just consistent required-marking + inline errors on top of it.
const VALIDATORS = {
  email: {
    test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    message: "Enter a valid email address — it needs an @ and a domain (e.g. name@gym.com).",
  },
  tel: {
    test: (v) => /^[0-9]{7,15}$/.test(v),
    message: "Phone numbers are digits only, 7-15 characters.",
  },
  number: {
    test: (v) => v === "" || /^-?\d*\.?\d*$/.test(v),
    message: "Numbers only — letters aren't allowed here.",
  },
};

export function FormField({
  label, type = "text", required = false, value, onChange, testId, placeholder, helperText, className = "", ...rest
}) {
  const [touched, setTouched] = useState(false);
  const validator = VALIDATORS[type];
  const isEmpty = value === "" || value === null || value === undefined;
  const missingRequired = required && touched && isEmpty;
  const badFormat = touched && !isEmpty && validator && !validator.test(value);
  const error = missingRequired ? "This field is required." : badFormat ? validator.message : null;

  const handleChange = (e) => {
    let v = e.target.value;
    // Numeric and phone fields block a bad keystroke immediately, rather than only flagging it
    // after blur — a phone number is digits-only, so letters never even land in the field.
    if (type === "number" && v !== "" && !/^-?\d*\.?\d*$/.test(v)) return;
    if (type === "tel" && v !== "" && !/^[0-9+\-\s]*$/.test(v)) return;
    onChange(v);
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <Label htmlFor={testId}>
          {label} {required && <span data-testid={testId ? `${testId}-required-mark` : undefined} style={{ color: "var(--scarlet-neg)" }}>*</span>}
        </Label>
      )}
      <Input
        id={testId}
        data-testid={testId}
        type={type === "number" ? "text" : type}
        inputMode={type === "number" ? "decimal" : type === "tel" ? "tel" : undefined}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        aria-invalid={!!error}
        className={error ? "border-red-500 focus-visible:ring-red-500 shake-on-error" : ""}
        {...rest}
      />
      {error && (
        <p data-testid={testId ? `${testId}-error` : undefined} className="text-xs font-medium" style={{ color: "var(--scarlet-neg)" }}>
          {error}
        </p>
      )}
      {!error && helperText && <p className="text-xs" style={{ color: "#68686E" }}>{helperText}</p>}
    </div>
  );
}

// Exposes the same "is this value currently valid" check a submit handler can reuse to avoid
// firing a mutation on an obviously-invalid required/format field (defense in depth — the real
// enforcement is still always server-side).
export function isFieldValid(type, required, value) {
  const isEmpty = value === "" || value === null || value === undefined;
  if (required && isEmpty) return false;
  if (!isEmpty && VALIDATORS[type] && !VALIDATORS[type].test(value)) return false;
  return true;
}
